import type { SummonKind } from "../../../data/portal-ffa-catalog";
import { t } from "../../../data/strings";
import type { SimMessage, WorldState } from "./types";
import {
  armyCount,
  C,
  ranking,
  teamPreserved,
} from "./shared";
import { summonFailReason } from "./summon";

function localizeMessage(world: WorldState, msg: SimMessage | null): string {
  if (!msg) return "";
  const params: Record<string, string | number> = { ...msg.params };
  if (msg.nameTeamId !== undefined) {
    const team = world.teams[msg.nameTeamId];
    if (team) {
      params.name = t(team.nameKey, team.nameParams);
    }
  }
  return t(msg.key, params);
}

const SUMMON_KINDS = [
  "guardian",
  "soldier",
  "raider",
  "chaser",
  "hero",
  "weapon",
] as const satisfies readonly SummonKind[];

export type HudSnapshot = ReturnType<typeof getHudSnapshot>;

export function getHudSnapshot(world: WorldState) {
  const player = world.teams[0];
  const ranks = ranking(world)
    .slice(0, 3)
    .map((team, i) => ({
      rank: i + 1,
      name: t(team.nameKey, team.nameParams),
      value: teamPreserved(team),
      isPlayer: team.isPlayer,
    }));
  const army = player ? armyCount(world, 0) : 0;
  const nextSigil =
    C.economy.sigilEveryMana -
    ((player?.ledger.lifetimeEarnedMana ?? 0) % C.economy.sigilEveryMana);
  const minimapDots = world.entities
    .filter(
      (e) =>
        e.role === "master" || e.role === "hero" || e.role === "weapon",
    )
    .map((e) => ({
      x: e.x,
      y: e.y,
      teamId: e.teamId,
      role: e.role,
      isSelf: e.teamId === 0 && e.role === "master",
    }));

  const minimapMana = world.orbs
    .filter((o) => world.time < o.expiresAt)
    .map((o) => ({
      x: o.x,
      y: o.y,
      ready: world.time >= o.collectableAt,
    }));

  const canAfford = {} as Record<SummonKind, ReturnType<typeof summonFailReason>>;
  for (const kind of SUMMON_KINDS) {
    canAfford[kind] = summonFailReason(world, 0, kind);
  }

  return {
    mana: player?.ledger.walletMana ?? 0,
    preserved: player ? teamPreserved(player) : 0,
    sigils: player?.sigils ?? 0,
    nextSigilNeed: nextSigil,
    lifetime: player?.ledger.lifetimeEarnedMana ?? 0,
    army,
    summonCd: player?.summonCd ?? 0,
    queueLen: player?.summonQueue.length ?? 0,
    queueDepth: C.summon.queueDepth,
    mapW: world.mapW,
    mapH: world.mapH,
    minimapMana,
    minimapDots,
    ranks,
    message: localizeMessage(world, world.message),
    alliance: world.alliance,
    warpCore: world.warpCore,
    pendingReenter: world.playerPendingReenter,
    runSummary: world.lastRunSummary,
    alive: player?.alive ?? false,
    canAfford,
  };
}
